import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import * as API from '../Utils/API.js';
import { Copilot } from '../Types/Copilot';
import { hoursForTier, RetainerTier, tierDisplayName } from '../Types/Retainer';
import { formatCardBrand, PaymentMethod } from '../Types/PaymentMethod';
import Steps from '../Components/Steps';
import { formatMultiParagraphString, htmlIf } from '../Utils/HTML';
import { DismissibleModal } from '../Components/Modal';
import { convertToUSDCents, CurrencyCode, ExchangeRate, formatUSDWithCents } from '../Utils/Currency';

type Props =
  { copilot: Copilot
  , tier: RetainerTier
  , paymentMethods: PaymentMethod[]
  , displayCurrency: CurrencyCode
  , exchangeRates: ExchangeRate
  , utmLcSource?: string
  }

enum Step
  { EnterDetails
  , SubmitPurchase
  }

const termsAndConditionsPath = '/terms-of-service'
const agentAccountBillingPath = '/agent/account/billing'
const agentRequestsPath = '/agent/requests'

const RetainerCheckout = (props: Props) => {
  const [currentStep, setCurrentStep] = useState(Step.EnterDetails);
  const [showErrors, setShowErrors] = useState(false);

  const [requestDescription, setRequestDescription] = useState('');

  const [availablePaymentMethods, setAvailablePaymentMethods] = useState(props.paymentMethods);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(props.paymentMethods[0]?.id);
  const [showAddPaymentMethodModal, setShowAddPaymentModal] = useState(false);

  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string>();

  function buildSubmitPostBody() {
    let postBody = {
      copilotId: props.copilot.id,
      tier: props.tier,
      requestDescription: requestDescription,
      paymentMethodId: selectedPaymentMethodId,
      utmLcSource: props.utmLcSource
    }

    return postBody;
  }

  // Used when a customer wiht an existing payment method (i.e. not their first purchase) wants to submit a purchase.
  function submitPurchase() {
    setIsPurchasing(true);

    const postBody = buildSubmitPostBody();

    API.post("agent_retainers_submit_purchase_path", postBody).then(function (result) {
      setIsPurchasing(false);

      if (result['error']) {
        if (result['message']) {
          setPurchaseError(result['message'])
        } else {
          setPurchaseError(
            "Something went wrong. Our team has been notified and will investigate the issue. If you continue to experience problems submitting a request, please email us at hello@letslucia.com"
          )
        }
      } else if (result['success'] === true) {
        window.location.href = agentRequestsPath
      }
    })
  }

  // Used when a customer with NO existing payment method (i.e. their first purchase) wants to submit a purchase.
  function submitFirstPurchase() {
    setIsPurchasing(true);

    const postBody = buildSubmitPostBody();

    API.post("agent_retainers_build_purchase_path", postBody).then(function (result) {
      if (result['error']) {
        if (result['message']) {
          setPurchaseError(result['message'])
        } else {
          setPurchaseError(
            "Something went wrong. Our team has been notified and will investigate the issue. If you continue to experience problems submitting a request, please email us at hello@letslucia.com"
          )
        }
      } else if (result['builtRequestCard']) {
        API.post("onboarding_setup_new_payment_method_path").then(function (newResult) {
          window.location.href = newResult['url']
        })
      }
    })
  }

  const backButton = (toStep: Step) => (
    <button className="btn btn-link p-0" onClick={() => setCurrentStep(toStep)}>
      <i className="ai-chevron-left fs-xxl" />
      <div className="fs-xl">Back</div>
    </button>
  )

  const ViewEnterDetailsPage = () => {
    function handleNextClicked() {
      if (requestDescription.length > 0) {
        setCurrentStep(Step.SubmitPurchase);
      } else {
        setShowErrors(true);
      }
    }

    return (
      <div className="mt-4">
        <h2 className="mb-4 mt-1">{tierDisplayName(props.tier)} Retainer with {props.copilot.preferredName}</h2>
        <h2 className="fs-xl">What are you looking to accomplish?</h2>
        <div className="fs-xl">Be specific and add details — this will help kickstart your work!</div>
        <textarea
          className="form-control mt-3 t--request-description"
          rows={5}
          placeholder="I need…"
          value={requestDescription}
          onChange={(event) => setRequestDescription(event.target.value)}
        />
        {htmlIf(showErrors && !(requestDescription.length > 0),
          <div className="fs-sm mt-1 text-danger fw-bold">Please enter a description.</div>
        )}
        <div className="fw-semibold mt-3">A good description includes:</div>
        <ul className="mb-0">
          <li>How you want to collaborate</li>
          <li>Weekly tasks & priorities</li>
          <li>Expected deliverables & outcomes</li>
          <li>Required skills & expertise</li>
          <li>Time commitment & schedule</li>
        </ul>
        <button className="btn btn-primary px-6 mt-3 t--step-3-next t--enter-description-next" onClick={handleNextClicked}>Continue</button>
      </div>
    )
  }

  function ViewSubmitPurchasePage() {
    function handleNewPaymentMethodAdded() {
      API.post("agent_fetch_payment_methods_path").then(function (result) {
        setAvailablePaymentMethods(result['paymentMethods']);
        setShowAddPaymentModal(false);
      })
    }

    function handleSubmitRequestClicked() {
      if (props.paymentMethods.length > 0) {
        submitPurchase();
      } else {
        submitFirstPurchase();
      }

    }

    const ViewAddPaymentMethodModal = () => {
      return (
        <DismissibleModal
          title={
            <h4>Add a new payment method</h4>
          }
          body={
            <div>
              Clicking below will open up your Billing settings page in a new tab, where you’ll be able to add
              a new payment method via Stripe. After adding your new payment method, return to this tab and select
              "Done."
            </div>
          }
          footer={
            <div className="text-start">
              <a className="btn btn-primary w-100 w-sm-auto mb-2 mb-sm-0"
                href={agentAccountBillingPath} target="_blank"
              >
                Add new payment method
              </a>
              <button className="btn btn-outline-success w-100 w-sm-auto ms-sm-2"
                onClick={handleNewPaymentMethodAdded}
              >
                Done
              </button>
            </div>
          }
          onDismiss={() => setShowAddPaymentModal(false)}
        />
      )
    }

    const ViewCopilot = () => (
      <>
        <div className="fs-md fw-semibold text-gray-900">CoPilot</div>
        <div className="d-flex align-items-center mt-1">
          <div className="avatar avatar-xs">
            <img className="avatar-img rounded-circle" src={props.copilot?.imageUrl}/>
          </div>
          <div className="fs-md ms-1">{props.copilot?.preferredName}</div>
        </div>
      </>
    )

    const ViewDisclaimers = () => (
      <>
        <div className="mt-2">
          Retainers are first billed when your CoPilot accepts the request to work with you, and every
          7 days following that. The upfront payments reserve availability from your CoPilot for the upcoming week.
        </div>
        {htmlIf(props.displayCurrency !== 'USD',
          <>
            <div className="mt-2">
              Note: while prices are displayed in your preferred currency, all transactions on Lucia are processed
              in USD using the latest exchange rates.
            </div>
            <div className="mt-1">
            {
              `You will be billed at a rate of approximately ${
                formatUSDWithCents(
                  convertToUSDCents(
                    props.displayCurrency,
                    (props.copilot.hourlyRateCents * hoursForTier(props.tier)),
                    props.exchangeRates
                  )
                )
              } USD/week.`
            }
            </div>
          </>
        )}
      </>
    )

    return (
      <div>
        { htmlIf(showAddPaymentMethodModal, <ViewAddPaymentMethodModal />)}
        <div className="mt-4">
          {backButton(Step.EnterDetails)}
          <h2 className="mb-1 mt-1">Confirm your retainer</h2>
          <div className="card-body py-3 border-top border-bottom">
            <div className="">
              <div className="fs-md fw-semibold text-gray-900">Title</div>
              <div className="fs-md">{tierDisplayName(props.tier)} Retainer – {hoursForTier(props.tier)} hours per week</div>
            </div>
            <div className="mt-2">
              <div className="fs-md fw-semibold text-gray-900">Description</div>
              <div className="fs-md">{formatMultiParagraphString(requestDescription)}</div>
            </div>
            <div className="mt-2">
              {ViewCopilot()}
            </div>
            <div className="mt-2">
              <div className="fs-md fw-semibold text-gray-900">Total</div>
              <div className="fs-md">{formatUSDWithCents(props.copilot.hourlyRateCents * hoursForTier(props.tier))}</div>
            </div>
            <div className="mt-3">
              <div className="d-flex align-items-center text-dark fw-semibold mb-1">
                <i className="ai-time fs-xxl me-1" />
                {hoursForTier(props.tier)} hours per week
              </div>
              {['Billed weekly', 'Flexible & convenient service', 'Cancel anytime'].map((text) => (
                <div key={text} className="d-flex align-items-center text-dark mb-1">
                  <i className="ai-check fs-xxl me-1" />
                  {text}
                </div>
              ))}
            </div>
            <div className="mt-2 fs-sm text-secondary">
              {ViewDisclaimers()}
            </div>
            <div className="mt-2 fs-sm">
              By continuing, you agree to the <a href={termsAndConditionsPath} target="_blank">Terms of Service</a>.
            </div>
          </div>
          <div className="card-body py-3">
            <h4 className="fw-bold">Payment</h4>
            { props.paymentMethods.length > 0
            ? <>
                <div className="">
                  <div className="card rounded-2">
                    <div className="card-header p-2 mx-0 w-100 text-dark fw-bold">
                      Saved payment options
                    </div>
                    <div className="card-body p-2">
                      {availablePaymentMethods.map((paymentMethod, index) => (
                        <div className={`form-check ${index === availablePaymentMethods.length - 1 ? 'mb-0' : 'mb-1'}`} key={paymentMethod.id}>
                          <input
                            className="form-check-input"
                            type="radio"
                            id={paymentMethod.id}
                            name="pm-radio"
                            value={paymentMethod.id}
                            onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                            checked={selectedPaymentMethodId === paymentMethod.id}
                            />
                          <label className="form-check-label" htmlFor={paymentMethod.id}>
                            <b>{formatCardBrand(paymentMethod.card.brand)} •••• {paymentMethod.card.last4}</b>
                            <br />
                            expiring {paymentMethod.card.expiration}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="card rounded-2 mt-3">
                    <div className="cursor-pointer d-flex card-body p-2 justify-content-between align-items-center fs-md text-dark fw-bold"
                      onClick={() => setShowAddPaymentModal(true)}
                    >
                      <div>Add credit/debit card</div>
                      <i className="ai-chevron-right" />
                    </div>
                  </div>
                </div>
              </>
            : <div className="mt-2 fs-md">
                Next, we’ll take you to Stripe to enter payment info.
                You won’t be charged until your CoPilot starts working.
              </div>
            }
            <div className="mt-3">
              <button
                className="btn btn-primary px-md-4 t--submit-request"
                onClick={handleSubmitRequestClicked}
                disabled={isPurchasing}
              >
                Continue
              </button>
            </div>
            {htmlIf(purchaseError !== null,
              <div className="mt-2 text-danger fw-bold">{purchaseError}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row justify-content-center" style={{minHeight: '100vh'}}>
      <div className="col-12 col-lg-10 col-xl-8">
        <Steps
          numSteps={2}
          currentStepNum={currentStep}
          containerClass="mb-2"
        />
      {(() => {
          switch (currentStep) {
            case Step.EnterDetails:
              return ViewEnterDetailsPage();
            case Step.SubmitPurchase:
              return ViewSubmitPurchasePage();
            default:
              return null;
          }
        })() as React.ReactNode}
      </div>
    </div>
  )
}

export default RetainerCheckout;
